import React, { useState, useEffect, useRef, memo, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { schemeCategory10 } from 'd3-scale-chromatic';
import { chain, groupBy, maxBy, minBy, sumBy } from 'lodash';
import {
  FormControlLabel,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  TextField,
  Radio,
  RadioGroup,
  Checkbox,
  Button,
  IconButton,
  Tooltip
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import * as d3 from 'd3';
import {
  Chart as ChartJS,
  Tooltip as ChartTooltip,
  Legend,
  LineController,
  BarController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  BarElement,
  CategoryScale,
  Title,
  Decimation,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import zoomPlugin from 'chartjs-plugin-zoom';

ChartJS.register(
  ChartTooltip, Legend, LineController, BarController, LineElement, PointElement,
  LinearScale, TimeScale, CategoryScale, BarElement, Title, Decimation, zoomPlugin
);

const DOWNSAMPLE_THRESHOLD = 10000;

// Helper function to apply min/max value filter
function applyValueFilter(rows, minValue, maxValue) {
  if (minValue === null && maxValue === null) return rows;
  
  return rows.filter(row => {
    const rowValue = parseFloat(row.value);
    if (isNaN(rowValue)) return true;
    
    if (minValue !== null && rowValue < minValue) return false;
    if (maxValue !== null && rowValue > maxValue) return false;
    return true;
  });
}

const lineConfig = {
  type: 'line',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    interaction: {
      mode: 'nearest',
      intersect: false
    },
    scales: {
      x: {
        type: 'time'
      }
    },
    plugins: {
      decimation: {
        enabled: true,
        threshold: DOWNSAMPLE_THRESHOLD,
        algorithm: 'lttb'
      },
      zoom: {
        pan: {
          enabled: true,
          modifierKey: 'ctrl'
        },
        zoom: {
          drag: {
            enabled: true,
          },
          mode: 'x',
          onZoomComplete: () => { /* set zoom state */ }
        },
      }
    }
  }
};

const barConfig = {
  type: 'bar',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        display: true,
        min: 0
      }
    }
  }
};

const histogramConfig = {
  type: 'bar',
  options: {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Bins'
        }
      },
      y: {
        title: {
          display: true,
          text: 'Frequency'
        }
      }
    }
  }
};

function getLineDatasets(records, opts) {
  const datasets = [];
  const byName = groupBy(records, 'name');
  let idx = 0;

  Object.keys(byName).forEach((name) => {
    const namedData = byName[name];
    const grouped = groupBy(namedData, o => {
      const deviceType = o.device.includes('Sap Flow') ? 'Sap Flow' : 'MFR node';
      return `${o.node}; ${deviceType} ${o.sensorId}`;
    });

    const hasGroup = Object.keys(grouped)[0] !== 'undefined';

    Object.keys(grouped).forEach((key) => {
      const d = grouped[key].map(o => ({
        x: new Date(o.ts).getTime(),
        y: o.value,
        meta: o.meta || {}
      })).sort((a, b) => a.x - b.x);

      datasets.push({
        label: name + (hasGroup ? `; ${key}` : ''),
        data: d,
        pointRadius: opts.showPoints ? 3 : 0,
        showLine: opts.showLines,
        borderColor: schemeCategory10[idx % 10],
        borderWidth: 2,
      });
      idx += 1;
    });
  });

  return datasets;
}

function getCountData(records) {
  const items = chain(records)
    .countBy('name')
    .map((val, key) => ({
      name: key,
      value: val
    }))
    .sortBy('value')
    .reverse()
    .value();

  const labels = items.map(o => o.name);
  const data = items.map(o => o.value);

  return {
    labels: labels,
    datasets: [{
      label: 'frequency',
      data,
      backgroundColor: labels.map((_, i) => schemeCategory10[i % 10])
    }]
  };
}

function getSumData(records) {
  const items = chain(records)
    .groupBy('name')
    .map((objs, key) => ({
      name: key,
      value: sumBy(objs, 'value')
    }))
    .sortBy('value')
    .reverse()
    .value();

  const labels = items.map(o => o.name);
  const data = items.map(o => o.value);

  return {
    labels: labels,
    datasets: [{
      label: 'sums',
      data,
      backgroundColor: labels.map((_, i) => schemeCategory10[i % 10])
    }]
  };
}

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const getBinLabels = (bins, binBy) => {
  if (binBy === 'value')
    return bins.map(({ x0, x1 }) => `${x0.toFixed(2)} - ${x1.toFixed(2)}`);
  else if (binBy === 'hour')
    return bins.map(({ x0, x1 }) => `${x0 < 10 ? '0' : ''}${x0}:00 - ${x1}:00`);
  else if (binBy === 'day')
    return bins.map((bin) => days[bin.x0]);
  else if (binBy === 'month')
    return bins.map((bin) => months[bin.x0]);
  else if (binBy === 'dayOfYear')
    return bins.map(({ x1 }) => `${x1 + 1}`);
  else if (binBy === 'time')
    return bins.map(({ x0 }) =>
      `${x0.toLocaleDateString()} ${x0.toLocaleTimeString()}`
    );
  else {
    return bins.map(({ x0, x1 }) => `${x0} - ${x1}`);
  }
};

function thresholdTime(n) {
  return (data, min, max) => {
    return d3.scaleUtc().domain([min, max]).ticks(n);
  };
}

function daysIntoYear(date) {
  return (
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    ) - Date.UTC(date.getFullYear(), 0, 0)) / 24 / 60 / 60 / 1000;
}

const timeAgg = {
  hour: (date) => date.getHours(),
  day: (date) => date.getDay(),
  dayOfYear: (date) => daysIntoYear(date),
  month: (date) => date.getMonth(),
};

const timeUpper = {
  hour: 24,
  day: 7,
  dayOfYear: 366,
  month: 12
};

function getHistogramData({ data, binBy = 'value', thresholds = 20 }) {
  let bins;

  if (binBy === 'value') {
    const min = Number(minBy(data, 'value').value);
    const max = Number(maxBy(data, 'value').value);
    bins = d3.bin()
      .value(d => d.value)
      .domain([min, max])
      .thresholds(thresholds)(data);
  } else if (binBy === 'time') {
    bins = d3.bin()
      .value(d => new Date(d.ts))
      .thresholds(thresholdTime(thresholds))(data);
  } else {
    bins = d3.bin()
      .value(d => timeAgg[binBy](new Date(d.ts)))
      .domain([0, timeUpper[binBy]])
      .thresholds(timeUpper[binBy])(data);
  }

  const counts = bins.map(bin => bin.length);

  return {
    labels: getBinLabels(bins, binBy),
    datasets: [{
      label: 'Count',
      data: counts,
      backgroundColor: schemeCategory10[0]
    }]
  };
}

function DataChart({ rows }) {
  const chartRef = useRef();
  const chartInstanceRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [disableDownsampling, setDisableDownsampling] = useState(false);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Filter state (synced with DataTable via URL)
  const [minValue, setMinValue] = useState(null);
  const [maxValue, setMaxValue] = useState(null);
  
  const [opts, setOpts] = useState({
    showLines: true,
    showPoints: false,
    type: 'timeseries',
    binBy: 'value',
    thresholds: 20
  });

  // Load filters from URL on mount and watch for changes
  useEffect(() => {
    const minParam = searchParams.get('min-value');
    const maxParam = searchParams.get('max-value');
    setMinValue(minParam ? parseFloat(minParam) : null);
    setMaxValue(maxParam ? parseFloat(maxParam) : null);
  }, [searchParams]);

  // Apply client-side filters (synced with DataTable)
  const filteredRows = useMemo(() => {
    return applyValueFilter(rows, minValue, maxValue);
  }, [rows, minValue, maxValue]);

  useEffect(() => {
    if (!chartRef.current || !filteredRows.length) return;

    // Add a small delay to prevent flicker
    setIsUpdating(true);
    const timer = setTimeout(() => {
      const { type, binBy, thresholds } = opts;
      let datasets;

      if (type === 'frequency')
        datasets = getCountData(filteredRows);
      else if (type === 'sum')
        datasets = getSumData(filteredRows);
      else if (type === 'histogram')
        datasets = getHistogramData({ data: filteredRows, binBy, thresholds });
      else
        datasets = getLineDatasets(filteredRows, opts);

      let config;
      if (type === 'frequency') {
        config = {
          ...barConfig,
          data: { ...datasets }
        };
      } else if (type === 'sum') {
        config = {
          ...barConfig,
          data: { ...datasets }
        };
      } else if (type === 'histogram') {
        config = {
          ...histogramConfig,
          data: { ...datasets }
        };
      } else {
        const modifiedLineConfig = JSON.parse(JSON.stringify(lineConfig));
        modifiedLineConfig.options.plugins.decimation.enabled = !disableDownsampling;
        modifiedLineConfig.options.plugins.zoom.zoom.onZoomComplete = () => setIsZoomed(true);
        config = {
          ...modifiedLineConfig,
          data: { datasets }
        };
      }

      // Destroy previous chart
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }

      // Create new chart
      chartInstanceRef.current = new ChartJS(chartRef.current, config);
      setIsUpdating(false);
    }, 50);

    return () => {
      clearTimeout(timer);
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [filteredRows, opts, disableDownsampling]);

  const handleOpts = (evt, option) => {
    const target = evt.target;
    const val = target.type === 'checkbox' ? target.checked : target.value;
    setOpts(prev => ({ ...prev, [option]: val }));
  };

  const handleZoomReset = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.resetZoom();
      setIsZoomed(false);
    }
  };

  const handleDownloadChart = () => {
    if (!chartInstanceRef.current || !chartRef.current) return;
    
    // Get the canvas and create a new one with white background
    const originalCanvas = chartRef.current;
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = originalCanvas.width;
    tempCanvas.height = originalCanvas.height;
    
    const ctx = tempCanvas.getContext('2d');
    // Fill with white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
    // Draw the chart on top
    ctx.drawImage(originalCanvas, 0, 0);
    
    // Download the image
    const url = tempCanvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `crocus-chart-${opts.type}-${new Date().toISOString()}.png`;
    a.click();
  };

  const { type, binBy = 'value', thresholds = 20 } = opts;

  return (
    <div className="mb-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-semibold">Query Timeseries</h3>
        <Tooltip title="Download chart as PNG">
          <IconButton 
            size="small" 
            onClick={handleDownloadChart}
            disabled={!filteredRows.length}
          >
            <CloudDownloadOutlinedIcon />
          </IconButton>
        </Tooltip>
      </div>
      
      <div className="relative border rounded bg-white" style={{ height: 400, padding: '10px' }}>
        {!rows.length ? (
          <div className="absolute inset-0 grid place-items-center text-gray-400 text-sm">
            Run a query to see data here
          </div>
        ) : (
          <div style={{ height: '100%', width: '100%', opacity: isUpdating ? 0.5 : 1, transition: 'opacity 0.1s' }}>
            <canvas ref={chartRef}></canvas>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2">
          {type === 'timeseries' && isZoomed && (
            <Button onClick={handleZoomReset} color="primary" variant="contained" size="small">
              Reset zoom
            </Button>
          )}
          {type === 'timeseries' && filteredRows.length >= DOWNSAMPLE_THRESHOLD && chartInstanceRef.current && (
            <div className="flex items-center gap-2">
              <Button onClick={() => setDisableDownsampling(prev => !prev)} size="small">
                {!disableDownsampling ? 'Disable' : 'Enable'} downsampling
              </Button>
              {!disableDownsampling && (
                <span className="text-xs text-gray-600">
                  Data downsampled using <a href="http://hdl.handle.net/1946/15343" target="_blank" rel="noreferrer" className="text-blue-600">LTTB</a>
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {type === 'timeseries' && (
            <div className="flex items-center">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={opts.showLines}
                    onChange={(evt) => handleOpts(evt, 'showLines')}
                    size="small"
                  />
                }
                label={<span className="text-sm">lines</span>}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={opts.showPoints}
                    onChange={(evt) => handleOpts(evt, 'showPoints')}
                    size="small"
                  />
                }
                label={<span className="text-sm">points</span>}
              />
            </div>
          )}

          {type === 'histogram' && (
            <div className="flex items-center gap-2">
              <RadioGroup
                row
                value={binBy}
                onChange={(evt) => handleOpts(evt, 'binBy')}
              >
                <FormControlLabel value="value" control={<Radio size="small" />} label={<span className="text-sm">By Value</span>} />
                <FormControlLabel value="hour" control={<Radio size="small" />} label={<span className="text-sm">By Hour</span>} />
                <FormControlLabel value="day" control={<Radio size="small" />} label={<span className="text-sm">By Day of Week</span>} />
                <FormControlLabel value="month" control={<Radio size="small" />} label={<span className="text-sm">By Month</span>} />
                <FormControlLabel value="time" control={<Radio size="small" />} label={<span className="text-sm">By Time</span>} />
              </RadioGroup>
              {['value', 'time'].includes(binBy) && (
                <TextField
                  type="number"
                  size="small"
                  value={thresholds}
                  onChange={(evt) => handleOpts(evt, 'thresholds')}
                  label="Thresholds"
                  style={{ width: 10 }}
                />
              )}
            </div>
          )}

          <FormControl variant="outlined" size="small" style={{ minWidth: 10 }}>
            <InputLabel id="chart-type">Type</InputLabel>
            <Select
              labelId="chart-type"
              value={type}
              onChange={evt => handleOpts(evt, 'type')}
              label="Type"
              style={{ height: '32px' }}
            >
              <MenuItem value='timeseries'>Timeseries</MenuItem>
              <MenuItem value='frequency'>Frequency</MenuItem>
              <MenuItem value='sum'>Sum</MenuItem>
              <MenuItem value='histogram'>Histogram (distribution)</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>
    </div>
  );
}

export default memo(DataChart, (prev, next) => prev.rows === next.rows);